import * as babylon from "@babylonjs/core";

import * as schema from "@/shared/state/visualization/schema/v1/three-d";

import type { PointCloudFrame } from "../../DataServer/messaging";

import { PointCloudFrameCache } from "./pointCloudFrameCache";
import { AsyncReloadable } from "./reloadable";

export type { PointCloudFrame };
export { ZERO_MEASURE_SPANS } from "../../DataServer/worker/pointCloud/packedPointCloud";

/**
 * A component drawing a PointCloud topic as points, one whole message at a
 * time: the latest message at or before the visualized time.
 *
 * Unlike the scalar components, the topic's data cannot be held for the whole
 * timeline (one message is up to millions of points), so what is held is the
 * topic's log-time index and a bounded {@link cache} of decoded frames. Each
 * frame the render pass resolves which message is {@link wanted}; the reload
 * poll uploads it from the cache into one mesh, or fetches that one message
 * through the worker, which decodes it into vertex buffers, and then fetches
 * the messages after it ahead of playback. Before the first message nothing
 * is drawn; after the last, the last cloud stays, like every other
 * sample-and-hold channel.
 *
 * The mesh is placed each frame from the entity's `GlobalPosition`, like a
 * `Mesh`, plus the frame's {@link PointCloudFrame.parentTranslation}.
 */
export interface PointCloud {
  spec: schema.PointCloud;
  mesh?: babylon.Mesh;
  material?: babylon.StandardMaterial;
  /** The topic's log-time index, loaded once per data spec. */
  index: { logtimes: bigint[]; load: AsyncReloadable };
  /**
   * Decoded frames by log time, the shown one among them, so a scrub or a
   * replay to a frame fetched before uploads it with no worker round trip,
   * and so frames can be fetched ahead of playback. The worker transfers
   * each frame's buffers to this thread and the cache owns them from then
   * on: Babylon keeps a reference to an uploaded array but never writes to
   * it, so showing a cached frame again re-uploads the same array. Cleared
   * when the component is disposed — a data-spec change replaces the
   * component — and kept across a display-only change, which leaves
   * positions untouched.
   */
  cache: PointCloudFrameCache;
  /**
   * Log times that could not be fetched or decoded once another frame had
   * landed, so fetches ahead skip them. A wanted frame is asked for anyway,
   * once each time it comes to be wanted, since the failure may have been the
   * network's. A failure before any frame landed fails the {@link index} load
   * instead, which ends the cloud's fetching for good, as a position whose
   * cache fetch threw: only a data spec change, which recreates the component,
   * tries again.
   */
  failed: Set<bigint>;
  /**
   * The frame the render pass wants drawn: the log time of the latest message
   * at or before the time, or undefined before the first message. Written every
   * frame by `updatePointClouds`, read by the reload poll.
   */
  wanted?: bigint;
  /**
   * The one in-flight fetch — of the wanted frame, or of one ahead of it —
   * so a frame is requested once. Never aborted for a newer {@link wanted}:
   * what it brings lands in the cache, and the newest want is served on the
   * poll after it lands. Dropped only when nothing is wanted or the
   * component goes away.
   */
  fetch?: { logTime: bigint; abortController: AbortController };
  /**
   * A frame not yet uploaded: just fetched, taken from the cache, or an empty
   * one standing in for a wanted message that failed.
   */
  pending?: PointCloudFrame;
  /**
   * What the mesh currently shows. The whole frame is kept, not only its log
   * time and count, because a display-only change — flat to rgb — has to put
   * the vertex colors back without refetching, and Babylon already holds the
   * same arrays for its updatable buffers, so this costs no extra copy.
   * `parentTranslation` is the frame's as a Babylon vector, allocated once
   * here rather than every frame in `applyRenderOrigin`.
   */
  current?: { frame: PointCloudFrame; parentTranslation: babylon.Vector3 };
  /**
   * The box every frame shown so far fits in, in the entity's frame — each
   * frame's vertices plus the parent translation they are relative to. The
   * camera envelope
   * measures the cloud by this rather than by the shown frame: the frames of
   * one sweep differ a little in size from message to message, and sizing the
   * scene to each would re-derive the zoom limits, the fog and the grid at
   * message rate. Only ever grows; a data-spec change replaces the component.
   */
  extent?: { min: babylon.Vector3; max: babylon.Vector3 };
  /** Display spec changed: re-apply color/pointSize without refetching. */
  displayStale: boolean;
}

/**
 * A point cloud for `spec` with nothing loaded yet: the index stale, no
 * frames held, nothing shown.
 */
export function createPointCloud(spec: schema.PointCloud): PointCloud {
  return {
    spec,
    index: {
      logtimes: [],
      load: { state: "stale", abortController: new AbortController() },
    },
    cache: new PointCloudFrameCache(),
    failed: new Set(),
    displayStale: false,
  };
}

/**
 * Grows {@link PointCloud.extent} to cover the box `min`..`max`, in the
 * entity's frame. Returns whether it grew, which is when the scene's content
 * changed as far as the camera is concerned.
 */
export function growExtent(
  cloud: PointCloud,
  min: babylon.Vector3,
  max: babylon.Vector3,
): boolean {
  const extent = cloud.extent;
  if (!extent) {
    cloud.extent = { min: min.clone(), max: max.clone() };
    return true;
  }
  if (
    min.x >= extent.min.x &&
    min.y >= extent.min.y &&
    min.z >= extent.min.z &&
    max.x <= extent.max.x &&
    max.y <= extent.max.y &&
    max.z <= extent.max.z
  ) {
    return false;
  }
  extent.min.minimizeInPlace(min);
  extent.max.maximizeInPlace(max);
  return true;
}
